import salesforceService from './salesforceService';
import indexedDBService from './indexedDBService';

class DataSyncService {
  constructor() {
    this.syncInterval = 5 * 60 * 1000; // 5 minutes
    this.MAX_DATA_AGE = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
    this.syncTimer = null;
    this.isSyncing = false;
  }

  async startSync() {
    if (this.syncTimer) {
      return;
    }

    // Initial sync
    await this.syncData();

    // Set up periodic sync
    this.syncTimer = setInterval(() => {
      this.syncData();
    }, this.syncInterval);
  }

  stopSync() {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  }

  async syncData() {
    if (this.isSyncing) {
      console.log('Sync already in progress, skipping...');
      return;
    }

    // Check if data is older than 6 hours
    const lastSyncTime = await this.getLastSyncTime();
    const currentTime = Date.now();
    const shouldForceSync = !lastSyncTime || (currentTime - lastSyncTime) > this.MAX_DATA_AGE;

    if (!shouldForceSync) {
      console.log('Data is fresh, skipping sync');
      return;
    }

    this.isSyncing = true;
    const syncStartTime = Date.now();
    
    try {
      console.log('Starting data sync...');
      
      // Add timeout to prevent hanging (10 minutes max)
      const SYNC_TIMEOUT = 10 * 60 * 1000;
      const syncPromise = salesforceService.batchQuery();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Sync timeout after 10 minutes')), SYNC_TIMEOUT)
      );
      
      const { clients, locations } = await Promise.race([syncPromise, timeoutPromise]);
      
      const syncDuration = Date.now() - syncStartTime;
      console.log(`Sync completed in ${(syncDuration / 1000).toFixed(1)}s`);
      console.log('Received data from Salesforce:', { 
        clientsCount: clients?.length || 0, 
        locationsCount: locations?.length || 0,
        sampleClient: clients?.[0],
        sampleLocation: locations?.[0]
      });

      // Clear existing data and save new data
      // console.log('Clearing existing data...');
      await Promise.all([
        indexedDBService.clearClients(),
        indexedDBService.clearLocations()
      ]);

      // Add lastSyncedAt timestamp to each record
      const timestamp = new Date().toISOString();
      const clientsWithTimestamp = (clients || []).map(client => ({
        ...client,
        lastSyncedAt: timestamp
      }));
      const locationsWithTimestamp = (locations || []).map(location => ({
        ...location,
        lastSyncedAt: timestamp
      }));

      // console.log('Saving new data...');
      await Promise.all([
        indexedDBService.saveClients(clientsWithTimestamp),
        indexedDBService.saveLocations(locationsWithTimestamp)
      ]);

      // console.log(`Synced ${clients?.length || 0} clients and ${locations?.length || 0} locations`);

      // Dispatch event to notify components
      window.dispatchEvent(new CustomEvent('datasynccomplete'));
      console.log('Data sync completed successfully');
    } catch (error) {
      const syncDuration = Date.now() - syncStartTime;
      console.error(`Sync failed after ${(syncDuration / 1000).toFixed(1)}s:`, error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status
      });
      throw error;
    } finally {
      this.isSyncing = false;
    }
  }

  async getLastSyncTime() {
    const [clients, locations] = await Promise.all([
      indexedDBService.getAllClients(),
      indexedDBService.getAllLocations()
    ]);

    if (clients.length === 0 && locations.length === 0) {
      return null;
    }

    const clientLastSync = clients.length > 0 
      ? Math.max(...clients.map(c => new Date(c.lastSyncedAt).getTime()))
      : 0;
    
    const locationLastSync = locations.length > 0
      ? Math.max(...locations.map(l => new Date(l.lastSyncedAt).getTime()))
      : 0;

    return Math.max(clientLastSync, locationLastSync);
  }
}

const dataSyncService = new DataSyncService();
export default dataSyncService; 